import { polygon as turfPolygon, featureCollection } from '@turf/helpers';
import { union } from '@turf/union';
import { area as turfArea } from '@turf/area';
const EARTH_RADIUS_METERS = 6378137;
// boundingExtentMeters returns the east-west and north-south size of the
// lat/lng bounding box around coords, using an equirectangular approximation.
export function boundingExtentMeters(coords) {
    if (coords.length === 0)
        return { eastWestMeters: 0, northSouthMeters: 0 };
    let minLat = coords[0].lat;
    let maxLat = coords[0].lat;
    let minLng = coords[0].lng;
    let maxLng = coords[0].lng;
    for (const c of coords) {
        minLat = Math.min(minLat, c.lat);
        maxLat = Math.max(maxLat, c.lat);
        minLng = Math.min(minLng, c.lng);
        maxLng = Math.max(maxLng, c.lng);
    }
    const toRad = Math.PI / 180;
    const midLatRad = ((minLat + maxLat) / 2) * toRad;
    return {
        eastWestMeters: (maxLng - minLng) * toRad * EARTH_RADIUS_METERS * Math.cos(midLatRad),
        northSouthMeters: (maxLat - minLat) * toRad * EARTH_RADIUS_METERS,
    };
}
// computeCopyOffsetLatLng returns the lat/lng delta used when duplicating a
// drawn item (polygon, line, circle): the copy lands due east, just past the
// original's east-west extent plus a gap of 10% of its larger extent (at
// least 1 m), so it sits beside the original at any size. Shared across tools
// so every "Duplicate" behaves identically.
export function computeCopyOffsetLatLng(centerLat, extentEastWestMeters, extentNorthSouthMeters) {
    const gap = Math.max(0.1 * Math.max(extentEastWestMeters, extentNorthSouthMeters), 1);
    const meters = extentEastWestMeters + gap;
    const dLng = (meters / (EARTH_RADIUS_METERS * Math.cos(centerLat * Math.PI / 180))) * 180 / Math.PI;
    return { dLat: 0, dLng };
}
// parsePolygonRing pulls the exterior ring (as {lat,lng}[]) out of a GeoJSON
// Polygon string. Returns null if it can't be parsed.
export function parsePolygonRing(geojson) {
    var _a;
    try {
        const g = JSON.parse(geojson);
        const coords = (_a = g === null || g === void 0 ? void 0 : g.coordinates) === null || _a === void 0 ? void 0 : _a[0];
        if (!Array.isArray(coords))
            return null;
        return coords.map((pt) => ({ lng: pt[0], lat: pt[1] }));
    }
    catch (_b) {
        return null;
    }
}
// ringToPolygonGeoJSON serializes a {lat,lng}[] ring to a closed GeoJSON Polygon.
export function ringToPolygonGeoJSON(ring) {
    const coords = ring.map((c) => [c.lng, c.lat]);
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (coords.length > 0 && (first[0] !== last[0] || first[1] !== last[1])) {
        coords.push([first[0], first[1]]);
    }
    return JSON.stringify({ type: 'Polygon', coordinates: [coords] });
}
// nearestPointOnSegment returns the closest point on segment a–b to p, treating
// lat/lng as planar (fine at the small scales used for snapping a vertex).
function nearestPointOnSegment(p, a, b) {
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    const len2 = dx * dx + dy * dy;
    let t = len2 === 0 ? 0 : ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return { lng: a.lng + t * dx, lat: a.lat + t * dy };
}
/**
 * clampPointToPolygon returns `point` unchanged if it is inside `ring`, otherwise
 * the nearest point on the ring's boundary. Used to confine DEM area-of-interest
 * drawing to the uploaded coverage (snap-to-edge).
 */
export function clampPointToPolygon(point, ring) {
    if (ring.length < 3)
        return point;
    if (isPointInPolygon(point, ring))
        return point;
    let best = point;
    let bestDist = Infinity;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const np = nearestPointOnSegment(point, a, b);
        const d = (np.lng - point.lng) ** 2 + (np.lat - point.lat) ** 2;
        if (d < bestDist) {
            bestDist = d;
            best = np;
        }
    }
    return best;
}
/**
 * buildDemAoiBounds turns an uploaded-boundary area of interest into the requested
 * (property) and buffer (extended) GeoJSON the DEM finalize expects. The extended
 * region is the full DEM coverage, so it captures all surrounding terrain for
 * hydrology while never extending past the uploaded data. Falls back to the AOI
 * itself when the DEM coverage is unavailable.
 */
export function buildDemAoiBounds(aoiRing, demBoundsGeoJSON) {
    if (aoiRing.length < 3) {
        throw new Error('Area of interest needs at least 3 points');
    }
    const requestedBoundsGeoJSON = ringToPolygonGeoJSON(aoiRing);
    return {
        requestedBoundsGeoJSON,
        bufferBoundsGeoJSON: demBoundsGeoJSON || requestedBoundsGeoJSON,
    };
}
/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param point The point to test
 * @param polygon Array of polygon vertices (must be closed or will be auto-closed)
 * @returns true if point is inside polygon
 */
export function isPointInPolygon(point, polygon) {
    if (polygon.length < 3)
        return false;
    let inside = false;
    const x = point.lng;
    const y = point.lat;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lng;
        const yi = polygon[i].lat;
        const xj = polygon[j].lng;
        const yj = polygon[j].lat;
        const intersect = ((yi > y) !== (yj > y)) &&
            (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect)
            inside = !inside;
    }
    return inside;
}
/**
 * Check if a line segment intersects with a polygon
 * Uses both endpoint containment and edge intersection tests
 */
export function doesLineIntersectPolygon(start, end, polygon) {
    if (polygon.length < 3)
        return false;
    // Check if either endpoint is inside the polygon
    if (isPointInPolygon(start, polygon) || isPointInPolygon(end, polygon)) {
        return true;
    }
    // Check if the line segment intersects any polygon edge
    for (let i = 0; i < polygon.length; i++) {
        const j = (i + 1) % polygon.length;
        if (lineSegmentsIntersect(start.lng, start.lat, end.lng, end.lat, polygon[i].lng, polygon[i].lat, polygon[j].lng, polygon[j].lat)) {
            return true;
        }
    }
    return false;
}
/**
 * Check if a polyline (series of connected line segments) intersects a polygon
 * Used for checking if contour lines intersect a polygon boundary
 */
export function doesPolylineIntersectPolygon(polyline, polygon) {
    if (polyline.length < 2 || polygon.length < 3)
        return false;
    // Check each segment of the polyline
    for (let i = 0; i < polyline.length - 1; i++) {
        if (doesLineIntersectPolygon(polyline[i], polyline[i + 1], polygon)) {
            return true;
        }
    }
    // Also check if any polyline vertex is inside the polygon
    for (const point of polyline) {
        if (isPointInPolygon(point, polygon)) {
            return true;
        }
    }
    return false;
}
/**
 * Check if two line segments intersect
 * Uses cross product method
 */
function lineSegmentsIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
    const d1 = direction(x3, y3, x4, y4, x1, y1);
    const d2 = direction(x3, y3, x4, y4, x2, y2);
    const d3 = direction(x1, y1, x2, y2, x3, y3);
    const d4 = direction(x1, y1, x2, y2, x4, y4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
        return true;
    }
    if (d1 === 0 && onSegment(x3, y3, x4, y4, x1, y1))
        return true;
    if (d2 === 0 && onSegment(x3, y3, x4, y4, x2, y2))
        return true;
    if (d3 === 0 && onSegment(x1, y1, x2, y2, x3, y3))
        return true;
    if (d4 === 0 && onSegment(x1, y1, x2, y2, x4, y4))
        return true;
    return false;
}
/**
 * Calculate cross product direction
 */
function direction(xi, yi, xj, yj, xk, yk) {
    return (xk - xi) * (yj - yi) - (xj - xi) * (yk - yi);
}
/**
 * Check if point (xk, yk) lies on segment from (xi, yi) to (xj, yj)
 */
function onSegment(xi, yi, xj, yj, xk, yk) {
    return Math.min(xi, xj) <= xk && xk <= Math.max(xi, xj) &&
        Math.min(yi, yj) <= yk && yk <= Math.max(yi, yj);
}
/**
 * Calculate the area of a polygon in square meters using spherical geometry
 * Uses the spherical excess formula (Girard's theorem)
 */
// Build a closed [[lng,lat], ...] ring for turf. Inserts a closing point if
// the input doesn't already loop back to its first vertex.
const toClosedRing = (coords) => {
    const ring = coords.map(c => [c.lng, c.lat]);
    if (ring.length === 0)
        return ring;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]]);
    }
    return ring;
};
// Build a turf polygon feature for a polygon-with-holes. Returns null if the
// exterior ring is too small to be a valid polygon.
const toTurfPolygon = (poly) => {
    var _a;
    if (!poly.exterior || poly.exterior.length < 3)
        return null;
    const rings = [toClosedRing(poly.exterior)];
    for (const hole of (_a = poly.holes) !== null && _a !== void 0 ? _a : []) {
        if (hole && hole.length >= 3)
            rings.push(toClosedRing(hole));
    }
    return turfPolygon(rings);
};
// Sum of (exterior − holes) areas for a polygon-with-holes, falling back to
// spherical-excess on each ring. Used as a fallback when turf union fails.
const polygonWithHolesAreaM2 = (poly) => {
    var _a;
    if (!poly.exterior || poly.exterior.length < 3)
        return 0;
    let area = calculatePolygonAreaM2(poly.exterior);
    for (const hole of (_a = poly.holes) !== null && _a !== void 0 ? _a : []) {
        if (hole && hole.length >= 3)
            area -= calculatePolygonAreaM2(hole);
    }
    return Math.max(0, area);
};
export function calculateBufferPolygonsAreaM2(polys) {
    const valid = polys.filter(p => p && p.exterior && p.exterior.length >= 3);
    if (valid.length === 0)
        return 0;
    if (valid.length === 1) {
        const f = toTurfPolygon(valid[0]);
        if (f) {
            try {
                return turfArea(f);
            }
            catch ( /* fall through */_a) { /* fall through */ }
        }
        return polygonWithHolesAreaM2(valid[0]);
    }
    try {
        const features = [];
        for (const poly of valid) {
            const f = toTurfPolygon(poly);
            if (f)
                features.push(f);
        }
        if (features.length === 0)
            return 0;
        const merged = union(featureCollection(features));
        if (merged)
            return turfArea(merged);
    }
    catch (_b) {
        // Fall through to per-polygon sum (over-counts overlaps, but rare).
    }
    return valid.reduce((sum, p) => sum + polygonWithHolesAreaM2(p), 0);
}
// Legacy helper kept so call sites that haven't migrated to the
// polygon-with-holes shape still work — wraps each input ring as a hole-less
// polygon.
export function calculateMergedPolygonAreaM2(polygons) {
    return calculateBufferPolygonsAreaM2(polygons.map(exterior => ({ exterior, holes: [] })));
}
export function calculatePolygonAreaM2(coords) {
    if (coords.length < 3)
        return 0;
    const EARTH_RADIUS = 6371000; // meters
    // Convert to radians and calculate spherical excess
    let total = 0;
    const n = coords.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const lat1 = coords[i].lat * Math.PI / 180;
        const lng1 = coords[i].lng * Math.PI / 180;
        const lat2 = coords[j].lat * Math.PI / 180;
        const lng2 = coords[j].lng * Math.PI / 180;
        total += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    const area = Math.abs(total * EARTH_RADIUS * EARTH_RADIUS / 2);
    return area;
}
/**
 * Filter an array of measurements to only include those inside a polygon
 * Both start and end points must be inside the polygon
 */
export function filterMeasurementsToPolygon(measurements, polygon) {
    return measurements.filter(m => {
        const start = { lat: m.startLat, lng: m.startLng };
        const end = { lat: m.endLat, lng: m.endLng };
        return isPointInPolygon(start, polygon) && isPointInPolygon(end, polygon);
    });
}
/**
 * Filter an array of paths to only include those that intersect a polygon
 * A path intersects if any of its segments intersect the polygon
 */
export function filterPathsToPolygon(paths, polygon) {
    return paths.filter(path => {
        const pathCoords = path.points.map(p => ({ lat: p.latitude, lng: p.longitude }));
        return doesPolylineIntersectPolygon(pathCoords, polygon);
    });
}
/**
 * Filter stream highlights to only include those that intersect a polygon
 */
export function filterStreamHighlightsToPolygon(highlights, polygon) {
    return highlights.filter(h => {
        if (!h.lineCoordinates || h.lineCoordinates.length < 2)
            return false;
        return doesPolylineIntersectPolygon(h.lineCoordinates, polygon);
    });
}
